import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { BlogPost } from "@/types/blogTypes";
import { getBlogPostBySlug } from "@/services/blogPostService";
import { isAdmin as checkIsAdmin } from "@/services/authService";
import { readPreloadedBlogPost } from "@/utils/ssr/preloadedState";

declare global {
  interface Window {
    __DEBUG_OVERLAY__?: {
      log: (message: string, level?: 'log' | 'info' | 'warn' | 'error') => void;
    };
  }
}

/**
 * Hook to load a blog post by slug
 */
export const useLoadBlogPost = (slug: string | undefined) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const initialPost = readPreloadedBlogPost(slug);
  const [post, setPost] = useState<BlogPost | null>(initialPost);
  const [isLoading, setIsLoading] = useState(() => Boolean(slug) && initialPost === null);
  const isDebugMode = typeof window !== 'undefined' && /([?&])debug=1\b/.test(window.location.search);

  const formatDebugArg = (arg: unknown) => {
    if (typeof arg === 'string') return arg;
    try {
      return JSON.stringify(arg);
    } catch {
      return String(arg);
    }
  };

  const debugLog = (...args: unknown[]) => {
    if (!isDebugMode) return;
    const message = args.map(formatDebugArg).join(' ');
    console.log('[useLoadBlogPost]', ...args);
    try {
      window.__DEBUG_OVERLAY__?.log('[useLoadBlogPost] ' + message, 'info');
    } catch (error) {
      console.error('[useLoadBlogPost] Failed to push to debug overlay', error);
    }
  };

  useEffect(() => {
    const loadPost = async () => {
      if (!slug) {
        setPost(null);
        setIsLoading(false);
        return;
      }

      if (initialPost && initialPost.slug === slug) {
        setPost(initialPost);
        setIsLoading(false);
        return;
      }

      debugLog('loadPost invoked for slug:', slug);
      
      try {
        setIsLoading(true);
        debugLog('Set loading state to true.');
        
        // First check for redirects before showing "not found"
        const checkRedirect = async () => {
          debugLog('Starting redirect check for slug:', slug);
          let controller: AbortController | undefined;
          let timeoutId: ReturnType<typeof setTimeout> | undefined;

          try {
            let signal: AbortSignal | undefined;

            if (typeof AbortSignal !== 'undefined' && typeof (AbortSignal as any).timeout === 'function') {
              signal = (AbortSignal as any).timeout(2000);
            } else if (typeof AbortController !== 'undefined') {
              controller = new AbortController();
              signal = controller.signal;
              timeoutId = setTimeout(() => {
                controller?.abort();
                debugLog('Manual abort triggered for redirect fetch after 2000ms.');
              }, 2000);
            } else {
              debugLog('AbortSignal/AbortController not available; proceeding without timeout.');
            }

            const requestInit: RequestInit = {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ path: `/blog/${slug}` }),
            };

            if (signal) {
              requestInit.signal = signal;
            }

            const response = await fetch(`/api/handle-redirect`, requestInit);
            debugLog('Redirect fetch resolved with status:', response.status);

            if (response.ok) {
              const data = await response.json();
              debugLog('Redirect response payload:', data);
              if (data && data.target_path && data.found !== false) {
                // Redirect found - navigate immediately
                debugLog('Redirect found. Navigating to:', data.target_path);
                navigate(data.target_path, { replace: true });
                return true; // Indicate redirect happened
              }
            }
          } catch (error) {
            if (error instanceof Error && error.name === 'AbortError') {
              console.warn('Redirect check timed out (manual controller) for:', slug);
              debugLog('Redirect fetch aborted (timeout).');
            }
            // Redirect check failed, continue with normal loading
            console.warn('Redirect check failed, continuing with post load:', error);
            debugLog('Redirect fetch failed. Error:', error instanceof Error ? error.message : error);
          } finally {
            if (timeoutId) {
              clearTimeout(timeoutId);
              debugLog('Cleared redirect timeout.');
            }
          }
          return false; // No redirect
        };

        // Check for redirect first
        const redirectHappened = await checkRedirect();
        debugLog('Redirect check complete. redirectHappened =', redirectHappened);
        if (redirectHappened) {
          debugLog('Redirect occurred. Aborting post load.');
          return; // Don't continue loading if redirect happened
        }

        // No redirect found, try to load the post
        debugLog('No redirect found. Fetching blog post data for slug:', slug);
        const postData = await getBlogPostBySlug(slug, { includeDrafts: true });
        debugLog('Blog post data loaded:', postData ? 'found' : 'not found');
        
        if (!postData) {
          // Add a small delay before showing error to allow any pending redirects
          setTimeout(() => {
            toast({
              title: "Post not found",
              description: "The blog post you're looking for could not be found.",
              variant: "destructive",
            });
            navigate("/blog/");
          }, 500);
          return;
        }
        
        if (!postData.published) {
          const isAdmin = await checkIsAdmin();

          if (!isAdmin) {
            navigate("/blog/");
            debugLog('User not admin and post unpublished; redirected to /blog/.');
            return;
          }
        }
        
        // Debug theme information
        console.log('BlogPost: Post loaded with theme:', postData.metadata?.theme);
        debugLog('Post theme metadata:', postData.metadata?.theme);
        
        setPost(postData);
      } catch (error) {
        console.error("Error loading blog post:", error);
        debugLog('Error loading post:', error instanceof Error ? error.message : error);

        toast({
          title: "Error loading post",
          description: "There was a problem loading this blog post. Please try again.",
          variant: "destructive",
        });
        navigate("/blog/");
      } finally {
        setIsLoading(false);
        debugLog('Set loading state to false.');
      }
    };
    
    loadPost();
  }, [initialPost, slug, navigate, toast]);

  return { post, isLoading };
};
